import { useCallback, useMemo, useState } from 'react';
import { Cookies } from 'react-cookie';

import { API_BASE_URL, environment, ERROR_MESSAGE } from '../constants';
import { ApplicationContainer } from '../providers';

interface IConfig {
  headers?: {
    Authorization?: string;
    'Content-Type'?: string;
  };
}

const HOST = API_BASE_URL[environment as keyof typeof API_BASE_URL];

export const useNetwork = () => {
  const { setNotification } = ApplicationContainer.useContainer();

  const [data, setData] = useState<any>(null);
  const [responseData, setResponseData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<any>(null);

  const accessToken = new Cookies().get('accessToken');

  const config: IConfig = useMemo(() => ({}), []);
  const postConfig: IConfig = useMemo(() => ({}), []);

  if (accessToken) {
    config.headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }
  postConfig.headers = {
    'Content-Type': 'application/json',
    ...(config.headers ?? {}),
  };

  const get = useCallback(
    async (url: string): Promise<any> => {
      setLoading(true);
      try {
        const response = await fetch(HOST + url, config);
        const apiData = await response.json();

        setStatus(response?.ok);
        setResponseData(apiData);
        let apiPayload = apiData.data ?? apiData;
        if (/chart|report-templates|documents|promo/gi.test(url)) {
          apiPayload = apiData;
        }

        setData(apiPayload);
        return apiPayload;
      } catch (err: any) {
        setError(err);
        setLoading(false);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [config, setNotification]
  );

  const post = useCallback(
    async (url: string, requestJSON: any) => {
      setLoading(true);
      try {
        const response = await fetch(HOST + url, {
          method: 'POST',
          ...postConfig,
          body: JSON.stringify(requestJSON),
        });
        const apiData = await response.json();
        setStatus(response?.ok);
        setResponseData(apiData);

        if (/4|5/gi.test(response.status.toString())) {
          setError(response.type);
          return response.type;
        }

        if (/chart|documents|copyTemplate/gi.test(url)) {
          setData(apiData);
          return apiData;
        }
        const apiResponse = apiData.data ?? apiData;
        setData(apiResponse);
        return apiResponse;
      } catch (err: any) {
        setError(err);
        setLoading(false);

        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postConfig, setNotification]
  );

  const formData = useCallback(
    async (url: string, requestJSON: any) => {
      setLoading(true);
      try {
        const response = await fetch(HOST + url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: requestJSON,
        });
        const apiData = await response.json();
        setStatus(response?.ok);
        setData(apiData);
        setLoading(false);
        return apiData;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [accessToken, setNotification]
  );

  const put = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await fetch(HOST + url, {
          method: 'PUT',
          ...postConfig,
          body: JSON.stringify(requestJSON),
        });
        setStatus(response?.ok);
        const apiData = await response.json();
        setStatus(response.status);
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postConfig, setNotification]
  );

  const remove = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await fetch(HOST + url, {
          method: 'DELETE',
          ...postConfig,
          body: JSON.stringify(requestJSON),
        });
        setStatus(response?.ok);
        const apiData = await response.json();
        setStatus(response.status);
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postConfig, setNotification]
  );

  const patch = useCallback(
    async (url: string, requestJSON?: any) => {
      try {
        const response = await fetch(HOST + url, {
          method: 'PATCH',
          ...postConfig,
          body: JSON.stringify(requestJSON),
        });
        setStatus(response?.ok);
        const apiData = await response.json();
        if (
          /chart|report-templates|sub-accounts|update-activation/gi.test(url)
        ) {
          setData(apiData);
          return apiData;
        }
        setResponseData(apiData);
        setStatus(response.status);
        setData(apiData.data);
        return apiData.data;
      } catch (err: any) {
        setError(err);
        if (err.message === ERROR_MESSAGE.status) {
          setNotification({
            type: 'error',
            messageComponent: ERROR_MESSAGE.msg,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [postConfig, setNotification]
  );

  return {
    get,
    post,
    formData,
    put,
    data,
    status,
    error,
    loading,
    setLoading,
    remove,
    patch,
    responseData,
  };
};
